// Count-ups (v4 motion): a figure marked data-count ("10x", "90%+", "92%")
// counts from zero to its value the first time it scrolls into view. The
// final text is what the server rendered, so crawlers and reduced-motion
// visitors read the real figure and the animation only ever ends on it.
// The v10 feedback (#3308) found the count over before the reader reached
// it: it used to start the moment half the figure showed at the bottom edge
// of the screen and be done in under a second. Now it waits until the figure
// has reached the middle of the screen (OBSERVER: half of it inside the top
// 65% of the viewport) and climbs for DURATION_MS on a gentle curve, so the
// reader sees it climb.
import { unclaimed } from "@lib/marketing/hooks";

export const DURATION_MS = 2400;
export const OBSERVER: IntersectionObserverInit = {
  threshold: 0.5,
  rootMargin: "0px 0px -35% 0px",
};

export interface Parsed {
  prefix: string;
  value: number;
  suffix: string;
}

export interface Clock {
  now(): number;
  schedule(callback: () => void): void;
}

export interface CounterHandle {
  dispose(): void;
}

export function parse(text: string): Parsed | null {
  const match = /^(\D*)(\d+)(.*)$/s.exec(text.trim());
  if (!match) return null;
  return { prefix: match[1], value: Number(match[2]), suffix: match[3] };
}

// Quadratic: the middle digits pass at a readable pace, the last ones settle.
export function easeOut(progress: number): number {
  return 1 - (1 - progress) ** 2;
}

const frames: Clock = {
  now: () => performance.now(),
  schedule: (callback) => {
    requestAnimationFrame(callback);
  },
};

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// Runs one count-up; the returned function cancels it and restores the text.
export function count(
  element: HTMLElement,
  duration: number = DURATION_MS,
  clock: Clock = frames,
): () => void {
  const original = element.textContent ?? "";
  const parsed = parse(original);
  if (!parsed) return () => {};

  let cancelled = false;
  const start = clock.now();
  const tick = () => {
    if (cancelled) return;
    const progress = Math.min((clock.now() - start) / duration, 1);
    if (progress >= 1) {
      element.textContent = original;
      return;
    }
    const value = Math.round(parsed.value * easeOut(progress));
    element.textContent = `${parsed.prefix}${value}${parsed.suffix}`;
    clock.schedule(tick);
  };
  tick();

  return () => {
    cancelled = true;
    element.textContent = original;
  };
}

export function mount(root: ParentNode = document, elements?: HTMLElement[]): CounterHandle {
  const targets =
    elements ?? unclaimed(Array.from(root.querySelectorAll<HTMLElement>("[data-count]")));
  const cancels: Array<() => void> = [];
  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      cancels.push(count(entry.target as HTMLElement));
    }
  }, OBSERVER);
  for (const element of targets) observer.observe(element);

  return {
    dispose() {
      observer.disconnect();
      for (const cancel of cancels) cancel();
    },
  };
}

export function boot(root: Document = document): CounterHandle | null {
  const elements = unclaimed(Array.from(root.querySelectorAll<HTMLElement>("[data-count]")));
  if (elements.length === 0 || prefersReducedMotion() || !("IntersectionObserver" in window))
    return null;

  const handle = mount(root, elements);
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  const onChange = (event: MediaQueryListEvent) => {
    if (!event.matches) return;
    handle.dispose();
    query.removeEventListener("change", onChange);
  };
  query.addEventListener("change", onChange);
  return handle;
}
